import { Formio } from 'react-formio';
import { getAuthItem } from '../utils/authStorage';

/** S3 folder for all accreditation form images (uploads + crops). Keep in sync with dashboard/server.js */
export const ACCREDITATION_S3_PREFIX = 'Accreditation/2028';

/** Max accreditation photo size. Keep in sync with ACCREDITATION_MAX_UPLOAD_BYTES in dashboard/server.js. */
export const ACCREDITATION_MAX_UPLOAD_MB = 5;

/** Allowed accreditation photo types for now (PNG, JPG). Keep in sync with ACCREDITATION_ALLOWED_MIME_TYPES in dashboard/server.js. */
export const ACCREDITATION_ALLOWED_FILE_TYPES = 'image/png,image/jpeg';

export const ACCREDITATION_FORM_ID = '68e6505866f25479291b933f';

/**
 * Form.io's URL provider deletes files with a bare XHR (no headers), so it cannot send the Bearer
 * token that DELETE /api/ems/uploads requires. Removing a photo only drops it from the form data;
 * the S3 object stays (originals are kept for crop revert, and an unsaved remove must not break the link).
 * Pass as the Form `fileService` option on the accreditation form; upload/download stay default.
 */
export const accreditationFileService = new Formio();
accreditationFileService.deleteFile = () => Promise.resolve();

export function getAccreditationUploadEndpoint(apiUrl) {
  const base = String(apiUrl || '').trim().replace(/\/$/, '');
  return base ? `${base}/ems/uploads` : '/api/ems/uploads';
}

function walkFormComponents(components, handler) {
  if (!Array.isArray(components)) return;
  components.forEach((comp) => {
    if (!comp) return;
    handler(comp);
    if (Array.isArray(comp.components)) walkFormComponents(comp.components, handler);
    if (Array.isArray(comp.columns)) {
      comp.columns.forEach((col) => walkFormComponents(col?.components, handler));
    }
    if (Array.isArray(comp.rows)) {
      comp.rows.forEach((row) => {
        if (Array.isArray(row)) {
          row.forEach((cell) => walkFormComponents(cell?.components, handler));
        }
      });
    }
  });
}

/**
 * Route Form.io file fields on the accreditation form through /api/ems/uploads
 * so the server stores objects under Accreditation/2028 in S3 only.
 * Fetch streams the object same-origin via /api/ems/uploads/Accreditation/2028/...
 * (optional AWS_S3_SIGNED_REDIRECT=true uses a 302 to a signed S3 URL).
 * /api/ems/uploads requires JWT auth on POST; Bearer is attached via options.headers.
 */
export function configureAccreditationFormFileUploads(components, apiUrl) {
  const uploadUrl = getAccreditationUploadEndpoint(apiUrl);
  const token = getAuthItem('token');
  walkFormComponents(components, (comp) => {
    if (comp.type !== 'file') return;
    // Force server-controlled upload → S3 Accreditation/2028 (ignore Form Builder local/url mix).
    comp.storage = 'url';
    comp.url = uploadUrl;
    comp.dir = ACCREDITATION_S3_PREFIX;
    comp.fileMaxSize = `${ACCREDITATION_MAX_UPLOAD_MB}MB`;
    comp.filePattern = ACCREDITATION_ALLOWED_FILE_TYPES;
    // Show the photo as a thumbnail (an <img>) instead of a file-name link. The View / Edit-Crop
    // buttons attach to that <img>, so they only render when image mode is on.
    if (comp.key === 'photo') {
      comp.image = true;
      comp.imageSize = '200';
    }
    // Avoid Form.io "upload only" hiding the stored file after save.
    if (comp.uploadOnly) comp.uploadOnly = false;
    if (token) {
      comp.options = {
        ...(comp.options || {}),
        headers: { Authorization: `Bearer ${token}` },
      };
    }
  });
}
